// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 644d8c3b-11b3-4ca6-ad46-ef6e78e068f8

// ---------------------------------------------------------------------------
// Primordia — Inventory
// ---------------------------------------------------------------------------
// The facet rail used to offer three cuts (Type, Lifecycle, Tags), which meant
// the questions a migration team actually asks first — who owns it, how
// critical is it, where is it hosted, what DR tier, TIME/6R, which wave —
// could only be answered by leaving for the Saved Views screen. This screen now
// carries a "majors" rail (the high-traffic cuts, one click each) plus a
// "More filters" builder that reaches EVERY field on the selected type.
//
// Two things drive the design:
//
//  1. Tenants differ enormously. One holds 7,305 ITComponent servers and no
//     Applications at all, so an "Application Type" facet would be dead weight
//     there. The majors are therefore derived from the schema and then ranked
//     and hidden according to the data that is actually present, not fixed.
//
//  2. Never silently drop a record for lacking a field. "(not set)" is a real,
//     selectable facet value — during discovery it is usually the most useful
//     one, because it is the list of what still has to be collected.
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, TextField, InputAdornment, List, ListItemButton,
  Divider, Button, Stack, Checkbox, FormControlLabel, Chip, Drawer, Badge, useMediaQuery,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, IconButton, Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FilterListIcon from '@mui/icons-material/FilterList';
import TuneIcon from '@mui/icons-material/Tune';
import DeleteIcon from '@mui/icons-material/Delete';
import ImportDialog from './ImportDialog';
import {
  allFieldsFor, FACT_SHEET_TYPES, FIELD_TYPES, PICKLISTS, resolveDrTier,
  resolvePicklist, TAG_GROUPS, typeMeta,
} from './schema';
import { TypeBadge, TagChips, CompletionBar, QualitySeal } from './ui';
import { exportCsv } from './exports';
import store from './store';
import HelpBubble from './HelpBubble';

// ---- Optimum palette -------------------------------------------------------
const NAVY = '#021F4B';
const BLUE = '#0D5B9A';
const GREY = '#5C6B7A';
const HEAD = 'sectionHeader.main';

// A missing value is a first-class facet value, never a reason to drop a row.
const NOT_SET = '(not set)';
const MAX_FACET_OPTIONS = 8;   // per facet, before "show all"
const PAGE_SIZE = 100;         // rendered rows; 7,305 of them would kill the DOM
const EMPTY = [];              // stable identity so memo deps do not churn

// ---------------------------------------------------------------------------
// Filter evaluation — a faithful MIRROR of src/cmdb/SavedViews.js
// ---------------------------------------------------------------------------
// valueOf / display / matchesFilter / VALUELESS / OPERATORS / SYNTH_FIELDS /
// buildFieldList are module-private in SavedViews.js and cannot be imported.
// They are reproduced here byte-for-byte in behaviour so that a filter built on
// this screen matches exactly the same fact sheets as the identical filter
// built in Saved Views. If SavedViews' semantics ever change, change them here
// too — the two must not drift.
// ---------------------------------------------------------------------------
const OPERATORS = ['is', 'is not', 'contains', 'is empty', 'is not empty', 'greater than', 'less than'];
const VALUELESS = ['is empty', 'is not empty'];

const SYNTH_FIELDS = [
  { key: '__name', label: 'Name', type: FIELD_TYPES.TEXT, section: 'Core' },
  { key: '__type', label: 'Fact Sheet Type', type: FIELD_TYPES.SELECT, picklist: FACT_SHEET_TYPES.map((t) => t.key), section: 'Core' },
  { key: '__completion', label: 'Completion %', type: FIELD_TYPES.PERCENT, section: 'Core' },
  { key: '__quality', label: 'Quality Seal', type: FIELD_TYPES.SELECT, picklist: PICKLISTS.qualitySeal, section: 'Core' },
];

function valueOf(fs, key) {
  if (!key) return undefined;
  if (key === '__name') return fs.name;
  if (key === '__type') return fs.type;
  if (key === '__completion') return fs.completion;
  if (key === '__quality') return fs.quality;
  if (key.indexOf('__tag:') === 0) {
    const group = key.slice(6);
    return (fs.tags || []).filter((t) => t.group === group).map((t) => t.value).join(', ');
  }
  const v = fs.fields ? fs.fields[key] : undefined;
  if ((v === undefined || v === null || v === '') && key === 'name') return fs.name;
  return v;
}

function display(v) {
  if (v === undefined || v === null || v === '') return '';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) return v.join(', ');
  return String(v);
}

function matchesFilter(fs, f) {
  if (!f.field || !f.op) return true;
  const raw = valueOf(fs, f.field);
  const s = display(raw);
  const target = (f.value === undefined || f.value === null) ? '' : String(f.value);
  if (VALUELESS.indexOf(f.op) === -1 && target === '') return true; // half-built row = inactive
  switch (f.op) {
    case 'is': return s.toLowerCase() === target.toLowerCase();
    case 'is not': return s.toLowerCase() !== target.toLowerCase();
    case 'contains': return s.toLowerCase().indexOf(target.toLowerCase()) !== -1;
    case 'is empty': return s === '';
    case 'is not empty': return s !== '';
    case 'greater than': {
      const a = Number(raw); const b = Number(target);
      return (Number.isFinite(a) && Number.isFinite(b)) ? a > b : s.localeCompare(target) > 0;
    }
    case 'less than': {
      const a = Number(raw); const b = Number(target);
      return (Number.isFinite(a) && Number.isFinite(b)) ? a < b : s.localeCompare(target) < 0;
    }
    default: return true;
  }
}

// The full field catalogue: synthetics, tag groups, then schema fields. Same
// shape as SavedViews' buildFieldList, widened to take several types because
// the Inventory can show more than one fact-sheet type at once.
function buildFieldList(types, tagGroups) {
  const seen = {};
  const out = [];
  SYNTH_FIELDS.forEach((f) => { seen[f.key] = true; out.push(f); });
  tagGroups.forEach((g) => {
    const key = `__tag:${g}`;
    if (seen[key]) return;
    seen[key] = true;
    out.push({ key, label: `Tag: ${g}`, type: FIELD_TYPES.SELECT, section: 'Tags', tagGroup: g });
  });
  types.forEach((type) => {
    allFieldsFor(type).forEach((f) => {
      if (f.type === FIELD_TYPES.RELATION || f.type === FIELD_TYPES.LIFECYCLE) return;
      if (seen[f.key]) return;
      seen[f.key] = true;
      out.push(f);
    });
  });
  return out;
}

// ---------------------------------------------------------------------------
// The "majors" — candidate promoted facets, addressed BY SCHEMA FIELD KEY.
// ---------------------------------------------------------------------------
// Only the keys are listed; every label, picklist and applicability is resolved
// through allFieldsFor() for the type(s) in play. A type that has no such field
// (an ITComponent has no `timeClassification`) simply never produces the facet,
// and a tenant that relabels a field gets its own label here for free.
//
// Several concepts are recorded under different keys on different types —
// migration wave is `cmWave` on an Application and `migrationWave` on a
// Deployment; the business owner is `applicationOwner` vs `owner`. Both keys
// are listed; whichever exists in the current field universe wins.
const MAJOR_FIELD_KEYS = [
  'lifecycleState',
  'applicationType',
  'businessCriticality',
  'hostingType',
  'locationType',
  'environment',
  'timeClassification',
  'sixRClassification',
  'cmDisposition',
  'cmWave',
  'migrationWave',
  'applicationOwner',
  'owner',
  'businessUnit',
];

// Facet headers come from the schema, with two cosmetic repairs: schema labels
// carry parenthetical hints too long for a 260px rail ("Hosting Type (On-Prem /
// SaaS / IaaS …)"), and `applicationType` is labelled literally "Type", which
// reads ambiguously beside the fact-sheet Type facet.
function facetTitle(field, typeLabel) {
  const label = String(field.label || field.key).replace(/\s*\(.*$/, '').trim();
  if (label.toLowerCase() === 'type') return `${typeLabel} Type`;
  return label;
}

// ---- single-pass tally helpers --------------------------------------------
function tally(map, v) {
  if (Array.isArray(v)) {
    for (let i = 0; i < v.length; i += 1) map.set(v[i], (map.get(v[i]) || 0) + 1);
    return;
  }
  map.set(v, (map.get(v) || 0) + 1);
}

function hasAny(v, selected) {
  if (Array.isArray(v)) {
    for (let i = 0; i < v.length; i += 1) if (selected.indexOf(v[i]) !== -1) return true;
    return false;
  }
  return selected.indexOf(v) !== -1;
}

// Facet section with counted, checkable options. Multi-select: values within
// one facet OR together, facets AND against each other.
function Facet({ title, options, selected, onToggle, note }) {
  const [expanded, setExpanded] = useState(false);
  if (!options.length) return null;
  const shown = expanded ? options : options.slice(0, MAX_FACET_OPTIONS);
  const hidden = options.length - shown.length;
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="overline" sx={{ color: HEAD, fontWeight: 700 }}>{title}</Typography>
      {note && (
        <Typography variant="caption" sx={{ display: 'block', color: GREY, mt: -0.5, mb: 0.5, fontStyle: 'italic' }}>
          {note}
        </Typography>
      )}
      {shown.map((o) => (
        <Box key={o.key} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <FormControlLabel
            sx={{ m: 0, minWidth: 0 }}
            control={<Checkbox size="small" checked={selected.indexOf(o.key) !== -1} onChange={() => onToggle(o.key)} />}
            label={(
              <span
                style={{
                  fontSize: 13,
                  color: o.key === NOT_SET ? GREY : undefined,
                  fontStyle: o.key === NOT_SET ? 'italic' : undefined,
                }}
              >
                {o.label}
              </span>
            )}
          />
          <Chip size="small" label={o.count} sx={{ height: 18, fontSize: 11 }} />
        </Box>
      ))}
      {hidden > 0 && (
        <Button size="small" sx={{ ml: 1, fontSize: 11 }} onClick={() => setExpanded(true)}>
          show {hidden} more
        </Button>
      )}
      {expanded && options.length > MAX_FACET_OPTIONS && (
        <Button size="small" sx={{ ml: 1, fontSize: 11 }} onClick={() => setExpanded(false)}>show less</Button>
      )}
    </Box>
  );
}

export default function Inventory({ tenantId, onOpen, onCreate, initialType = 'Application' }) {
  const [all, setAll] = useState([]);
  const [search, setSearch] = useState('');
  // One map for every facet: { facetKey: [selected values] }. Keeps the
  // single-pass evaluator generic instead of one useState per dimension.
  const [sel, setSel] = useState({ __type: [initialType] });

  // Applications and servers are DIFFERENT INVENTORIES, and the difference is
  // operational, not cosmetic: you clone an application list from the catalog,
  // you never clone servers -- those are discovered. Two nav entries land here
  // with different starting types, and this resets the facet when the entry
  // changes, because React keeps state across a re-render with the same
  // component and the screen would otherwise keep showing whichever list you
  // opened first.
  useEffect(() => {
    setSel((prev) => {
      const cur = prev.__type || EMPTY;
      if (cur.length === 1 && cur[0] === initialType) return prev;
      return { ...prev, __type: [initialType] };
    });
  }, [initialType]);
  const [filters, setFilters] = useState([]);   // the long tail (SavedViews grammar)
  const [importOpen, setImportOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const theme = useTheme();
  // A 260px fixed rail leaves nothing for the list on a phone, so on small
  // screens the facets move into a drawer behind a Filters button.
  const compact = useMediaQuery(theme.breakpoints.down('md'));

  // useCallback so the effect can depend on `reload` honestly instead of
  // suppressing the exhaustive-deps warning; it changes only when the tenant does.
  const reload = useCallback(() => store.listFactSheets(tenantId).then((rows) => {
    setAll(rows);
    // Some tenants hold no Applications at all. Landing on an empty screen
    // reads as "no data" rather than "wrong default", so drop a type
    // pre-selection that this tenant cannot satisfy.
    setSel((s) => {
      const t = s.__type || EMPTY;
      if (!t.length || t.some((k) => rows.some((r) => r.type === k))) return s;
      const next = { ...s };
      delete next.__type;
      return next;
    });
  }), [tenantId]);
  useEffect(() => { reload(); }, [reload]);

  const typeSel = sel.__type || EMPTY;

  // "Inventory" alone does not say WHAT is being inventoried, and this screen
  // serves every fact sheet type from one list — so a screenshot of servers and
  // a screenshot of applications were indistinguishable by their heading. When
  // the type facet narrows to exactly one type the heading names it, using the
  // type's own label from FACT_SHEET_TYPES so a relabelled type cannot leave a
  // stale title behind here. Several types selected (or none) is genuinely a
  // mixed list, and keeps the plain word.
  const headingLabel = (typeSel.length === 1
    && (FACT_SHEET_TYPES.find((t) => t.key === typeSel[0]) || {}).label) || '';
  const heading = headingLabel ? `${headingLabel} inventory` : 'Inventory';

  // What this tenant actually contains. One pass, but keyed to the data alone,
  // so it runs on load/import and never on a filter click.
  const present = useMemo(() => {
    const types = [];
    const groups = TAG_GROUPS.slice();
    const seenType = {};
    const seenGroup = {};
    groups.forEach((g) => { seenGroup[g] = true; });
    all.forEach((fs) => {
      if (fs.type && !seenType[fs.type]) { seenType[fs.type] = true; types.push(fs.type); }
      (fs.tags || []).forEach((t) => {
        if (t && t.group && !seenGroup[t.group]) { seenGroup[t.group] = true; groups.push(t.group); }
      });
    });
    return { types, groups };
  }, [all]);

  // Field universe = every field of every type currently in scope.
  const scopeTypes = useMemo(
    () => (typeSel.length ? typeSel : (present.types.length ? present.types : ['Application'])),
    [typeSel, present.types],
  );

  const fieldDefs = useMemo(() => {
    const m = new Map();
    scopeTypes.forEach((t) => {
      allFieldsFor(t).forEach((f) => { if (!m.has(f.key)) m.set(f.key, f); });
    });
    return m;
  }, [scopeTypes]);

  // ---- facet definitions (schema-derived) ----------------------------------
  const facetDefs = useMemo(() => {
    const typeLabel = scopeTypes.length === 1 ? typeMeta(scopeTypes[0]).label : 'Application';
    const defs = [{
      key: '__type',
      title: 'Fact Sheet Type',
      scoped: false,               // the type facet is what defines the scope
      labelOf: (v) => (v === NOT_SET ? v : typeMeta(v).label),
      get: (fs) => fs.type || NOT_SET,
    }];
    MAJOR_FIELD_KEYS.forEach((key) => {
      const f = fieldDefs.get(key);
      if (!f) return;
      defs.push({
        key,
        title: facetTitle(f, typeLabel),
        scoped: true,
        get: (fs) => display(valueOf(fs, key)) || NOT_SET,
      });
    });
    // DR Tier lives inconsistently across a tag group AND a field; resolveDrTier
    // is the reconciliation and is the ONLY way this screen reads it.
    defs.push({
      key: '__drTier',
      title: 'DR Tier',
      scoped: true,
      get: (fs) => resolveDrTier(fs) || NOT_SET,
    });
    present.groups.forEach((g) => {
      if (g === 'DR Tier') return;   // already covered, resolved, above
      defs.push({
        key: `__tag:${g}`,
        title: `Tag: ${g}`,
        scoped: true,
        get: (fs) => {
          const out = [];
          const tags = fs.tags || [];
          for (let i = 0; i < tags.length; i += 1) {
            if (tags[i] && tags[i].group === g) out.push(tags[i].value);
          }
          return out.length ? out : NOT_SET;
        },
      });
    });
    return defs;
  }, [fieldDefs, present.groups, scopeTypes]);

  // -------------------------------------------------------------------------
  // ONE pass over the rows produces: the filtered result set, every facet's
  // option list, and every facet's count. 7,305 rows x ~15 facets is a single
  // traversal, not one traversal per facet.
  //
  // Per row we extract each facet's value once into a reused scratch array,
  // then count how many facets that row fails:
  //   fails === 0            -> the row is in the result set, and it counts
  //                             towards every facet
  //   fails === 1 (facet i)  -> it counts towards facet i only, which is what
  //                             makes facet counts read "what you would get if
  //                             you also ticked this box"
  //   fails > 1              -> counts nowhere
  // A second tally (baseCounts, gated only by the type selection) is taken in
  // the same visit and drives which facets are worth showing and in what order,
  // so the rail does not reshuffle itself under the cursor as boxes are ticked.
  // -------------------------------------------------------------------------
  const facetView = useMemo(() => {
    const n = facetDefs.length;
    const baseCounts = [];
    const viewCounts = [];
    const active = [];
    for (let i = 0; i < n; i += 1) {
      baseCounts.push(new Map());
      viewCounts.push(new Map());
      active.push(sel[facetDefs[i].key] || EMPTY);
    }
    const q = search.trim().toLowerCase();
    const live = filters.filter((f) => f.field && f.op);
    const vals = new Array(n);
    const matched = [];

    for (let r = 0; r < all.length; r += 1) {
      const fs = all[r];

      // Hard gate: free-text search and the long-tail filters. These are not
      // facets, so they never get the "count as if unselected" treatment.
      let hardOk = true;
      if (q) {
        hardOk = (fs.name || '').toLowerCase().indexOf(q) !== -1
          || ((fs.fields && fs.fields.description) || '').toLowerCase().indexOf(q) !== -1;
      }
      for (let i = 0; hardOk && i < live.length; i += 1) {
        if (!matchesFilter(fs, live[i])) hardOk = false;
      }
      const typeOk = !typeSel.length || typeSel.indexOf(fs.type) !== -1;

      let fails = 0;
      let failIdx = -1;
      for (let i = 0; i < n; i += 1) {
        const v = facetDefs[i].get(fs);
        vals[i] = v;
        const s = active[i];
        if (s.length && !hasAny(v, s)) {
          fails += 1;
          failIdx = fails === 1 ? i : -1;
        }
      }
      for (let i = 0; i < n; i += 1) {
        if (!facetDefs[i].scoped || typeOk) tally(baseCounts[i], vals[i]);
        if (hardOk && (fails === 0 || failIdx === i)) tally(viewCounts[i], vals[i]);
      }
      if (hardOk && fails === 0) matched.push(fs);
    }

    const facets = facetDefs.map((d, i) => {
      const base = baseCounts[i];
      let total = 0;
      let top = 0;
      let nonEmpty = 0;
      base.forEach((c, k) => {
        total += c;
        if (c > top) top = c;
        if (k !== NOT_SET) nonEmpty += c;
      });
      const keys = Array.from(base.keys()).sort((x, y) => (
        (base.get(y) - base.get(x)) || String(x).localeCompare(String(y))
      ));
      const options = keys.map((k) => ({
        key: k,
        label: d.labelOf ? d.labelOf(k) : k,
        count: viewCounts[i].get(k) || 0,
      }));
      // How much does this facet actually discriminate? Balance says a facet
      // where everything shares one value is worthless; coverage says a facet
      // nobody has filled in is worthless; spread demotes free-text dimensions
      // (hundreds of distinct owners) that cannot be clicked through usefully.
      const distinct = options.length;
      const balance = total ? 1 - (top / total) : 0;
      const coverage = total ? nonEmpty / total : 0;
      const spread = distinct <= 1 ? 0 : Math.min(1, 12 / distinct);
      return {
        key: d.key,
        title: d.title,
        options,
        distinct,
        total,
        nonEmpty,
        uniform: distinct === 1,
        score: balance * coverage * (0.5 + (0.5 * spread)),
        order: i,
        isType: d.key === '__type',
      };
    });

    // Hide anything with no non-empty value at all in this tenant (an
    // Application Type facet in a servers-only tenant), unless the user has a
    // selection on it — a filter you cannot see is a filter you cannot remove.
    const visible = facets.filter((f) => f.nonEmpty > 0 || (sel[f.key] || EMPTY).length > 0);
    visible.sort((a, b) => {
      const ap = a.isType && a.distinct > 1 ? 1 : 0;
      const bp = b.isType && b.distinct > 1 ? 1 : 0;
      if (ap !== bp) return bp - ap;              // real type choice always leads
      if (b.score !== a.score) return b.score - a.score;
      return a.order - b.order;
    });
    return { facets: visible, rows: matched };
  }, [all, facetDefs, sel, search, filters, typeSel]);

  const rows = facetView.rows;

  // A new result set always starts at page one.
  useEffect(() => { setLimit(PAGE_SIZE); }, [rows]);

  // ---- long-tail filter plumbing (SavedViews grammar) ----------------------
  const advFields = useMemo(
    () => buildFieldList(scopeTypes, present.groups),
    [scopeTypes, present.groups],
  );
  const advFieldMap = useMemo(() => {
    const m = {};
    advFields.forEach((f) => { m[f.key] = f; });
    return m;
  }, [advFields]);

  const optionsFor = useCallback((fieldKey) => {
    const f = advFieldMap[fieldKey];
    if (!f) return [];
    if (f.tagGroup) {
      const seen = {};
      all.forEach((fs) => (fs.tags || []).forEach((t) => { if (t.group === f.tagGroup) seen[t.value] = true; }));
      return Object.keys(seen).sort();
    }
    if (f.type === FIELD_TYPES.BOOLEAN) return ['Yes', 'No'];
    if (f.picklist) return resolvePicklist(f.picklist);
    return [];
  }, [advFieldMap, all]);

  const addFilter = () => setFilters((fs) => fs.concat([{ field: '__name', op: 'contains', value: '' }]));
  const setFilter = (i, p) => setFilters((fs) => fs.map((f, n) => (n === i ? { ...f, ...p } : f)));
  const removeFilter = (i) => setFilters((fs) => fs.filter((_, n) => n !== i));

  // ---- selection plumbing --------------------------------------------------
  const toggleFacet = useCallback((key, value) => {
    setSel((s) => {
      const cur = s[key] || EMPTY;
      const next = cur.indexOf(value) === -1 ? cur.concat([value]) : cur.filter((x) => x !== value);
      const out = { ...s };
      if (next.length) out[key] = next; else delete out[key];
      return out;
    });
  }, []);

  const clearAll = () => { setSel({}); setFilters([]); setSearch(''); };

  // Active filter set, as removable chips. Built from facetDefs (not from the
  // visible rail) so a selection on a hidden facet is still removable.
  const chips = [];
  if (search.trim()) {
    chips.push({ id: '__search', label: `Search: “${search.trim()}”`, remove: () => setSearch('') });
  }
  facetDefs.forEach((d) => {
    (sel[d.key] || EMPTY).forEach((v) => chips.push({
      id: `${d.key}|${v}`,
      label: `${d.title}: ${d.labelOf ? d.labelOf(v) : v}`,
      remove: () => toggleFacet(d.key, v),
    }));
  });
  filters.forEach((f, i) => {
    if (!f.field || !f.op) return;
    const label = (advFieldMap[f.field] && advFieldMap[f.field].label) || f.field;
    const needsValue = VALUELESS.indexOf(f.op) === -1;
    if (needsValue && !String(f.value || '')) return;   // half-built row = inactive
    chips.push({
      id: `flt-${i}`,
      label: `${label} ${f.op}${needsValue ? ` ${f.value}` : ''}`,
      remove: () => removeFilter(i),
    });
  });

  const doExport = () => {
    exportCsv(`inventory-${tenantId}.csv`, rows, [
      { label: 'Type', value: 'type' },
      // The per-row tenant chip is rendered in the master workspace but the
      // export dropped it, so a cross-client CSV read as one client's estate.
      ...(tenantId === 'optimum' ? [{ label: 'Client', value: 'tenantId' }] : []),
      { label: 'Name', value: 'name' },
      { label: 'Description', value: (r) => r.fields?.description },
      { label: 'Lifecycle', value: (r) => r.fields?.lifecycleState },
      { label: 'TIME', value: (r) => r.fields?.timeClassification },
      { label: '6R', value: (r) => r.fields?.sixRClassification },
      { label: 'Hosting', value: (r) => r.fields?.hostingType },
      { label: 'Completion %', value: 'completion' },
      { label: 'Cell', value: (r) => (r.tags || []).find((t) => t.group === 'Cell')?.value },
      // Reconciled, not read straight off the tag — see resolveDrTier.
      { label: 'DR Tier', value: (r) => resolveDrTier(r) },
      { label: 'Managed By', value: (r) => (r.tags || []).find((t) => t.group === 'Managed By')?.value },
    ]);
  };

  const facetContent = (
    <>
      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6" sx={{ flex: 1 }}>Filters</Typography>
        {chips.length > 0 && <Button size="small" onClick={clearAll}>Clear all</Button>}
      </Stack>
      <Divider sx={{ mb: 2 }} />
      {facetView.facets.map((f) => (
        <Facet
          key={f.key}
          title={f.title}
          options={f.options}
          selected={sel[f.key] || EMPTY}
          onToggle={(v) => toggleFacet(f.key, v)}
          note={f.uniform ? `all ${f.total} records share this value` : ''}
        />
      ))}
      {!facetView.facets.length && (
        <Typography variant="body2" sx={{ color: GREY, mb: 2 }}>
          Nothing to facet on yet — this tenant has no fact sheets loaded.
        </Typography>
      )}
      <Divider sx={{ mb: 1.5 }} />
      <Button
        fullWidth size="small" variant="outlined" startIcon={<TuneIcon />}
        onClick={() => setMoreOpen(true)}
      >
        More filters{filters.length ? ` (${filters.length})` : ''}
      </Button>
      <Typography variant="caption" sx={{ display: 'block', color: GREY, mt: 0.5 }}>
        Every field on {scopeTypes.length === 1 ? typeMeta(scopeTypes[0]).label : 'the selected types'}.
      </Typography>
    </>
  );

  return (
    <Box sx={{ display: 'flex', gap: 2, p: { xs: 1, md: 2 } }}>
      {/* Facet rail — inline on desktop, drawer on mobile */}
      {!compact && (
        <Paper sx={{ width: 260, p: 2, flexShrink: 0, alignSelf: 'flex-start', position: 'sticky', top: 8, maxHeight: '92vh', overflowY: 'auto' }}>
          {facetContent}
        </Paper>
      )}
      <Drawer anchor="left" open={compact && filtersOpen} onClose={() => setFiltersOpen(false)}>
        <Box sx={{ width: 280, p: 2 }}>{facetContent}</Box>
      </Drawer>

      {/* Result list */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: HEAD }}>{heading}</Typography>
          <HelpBubble topic="inventory" />
          {compact && (
            <Badge badgeContent={chips.length} color="primary">
              <Button size="small" startIcon={<FilterListIcon />} variant="outlined"
                onClick={() => setFiltersOpen(true)}>Filters</Button>
            </Badge>
          )}
          <TextField
            size="small" placeholder="Search fact sheets…" value={search}
            onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 160 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
          {/* Authoring is disabled in the master workspace rather than silently
              redirected into a client. Say so, so it reads as a rule and not a
              broken button. */}
          <Tooltip title={tenantId === 'optimum'
            ? 'Switch to a client workspace to add records. The master workspace looks across clients; it does not hold records of its own.'
            : ''}>
            <span>
              <Button startIcon={<AddIcon />} variant="outlined" disabled={tenantId === 'optimum'} onClick={onCreate}>New</Button>
            </span>
          </Tooltip>
          <Tooltip title={tenantId === 'optimum'
            ? 'Switch to a client workspace to import. An import from master would have to land in some client’s estate, and choosing one for you is how data ends up in the wrong place.'
            : ''}>
            <span>
              <Button startIcon={<UploadFileIcon />} variant="outlined" disabled={tenantId === 'optimum'} onClick={() => setImportOpen(true)}>Import</Button>
            </span>
          </Tooltip>
          <Button startIcon={<DownloadIcon />} variant="contained" sx={{ bgcolor: BLUE, '&:hover': { bgcolor: NAVY } }} onClick={doExport}>Export CSV</Button>
        </Stack>

        {/* The active filter set, always visible and always removable. */}
        {chips.length > 0 && (
          <Stack direction="row" spacing={0.5} sx={{ mb: 1, flexWrap: 'wrap', rowGap: 0.5, alignItems: 'center' }}>
            {chips.map((c) => (
              <Chip key={c.id} size="small" label={c.label} onDelete={c.remove}
                sx={{ bgcolor: 'chipMuted.main', color: HEAD, maxWidth: 320 }} />
            ))}
            <Button size="small" onClick={clearAll}>Clear all</Button>
          </Stack>
        )}

        <Typography variant="body2" sx={{ color: GREY, mb: 1 }}>
          Showing <b>{rows.length.toLocaleString()}</b> of <b>{all.length.toLocaleString()}</b> fact sheets
          {chips.length > 0 ? ' (filtered)' : ''}
          {rows.length > limit ? ` · rendering the first ${limit.toLocaleString()}` : ''}
        </Typography>

        <Paper>
          <List disablePadding>
            {rows.slice(0, limit).map((fs, i) => (
              <React.Fragment key={fs.id}>
                {i > 0 && <Divider />}
                <ListItemButton onClick={() => onOpen(fs.id)} sx={{ alignItems: 'flex-start', py: 1.5, px: { xs: 1, md: 2 } }}>
                  <Box sx={{ mr: 1.5, mt: 0.5 }}><TypeBadge type={fs.type} /></Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                      <Typography sx={{ fontWeight: 700 }}>{fs.name}</Typography>
                      <TagChips tags={fs.tags} />
                      {tenantId === 'optimum' && (
                        <Chip size="small" variant="outlined" label={fs.tenantId} sx={{ height: 18, fontSize: 10 }} />
                      )}
                    </Stack>
                    <Typography variant="body2" sx={{ color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                      {fs.fields?.description}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 0.5 }}>
                      <CompletionBar value={fs.completion} />
                      {fs.fields?.timeClassification && <Chip size="small" label={`TIME: ${fs.fields.timeClassification}`} sx={{ height: 20 }} />}
                      <QualitySeal value={fs.quality} />
                    </Stack>
                  </Box>
                </ListItemButton>
              </React.Fragment>
            ))}
            {!rows.length && (
              <Box sx={{ p: 3, textAlign: 'center', color: GREY }}>
                <Typography variant="body2">
                  No fact sheets match these filters
                  {all.length ? ` — ${all.length.toLocaleString()} in the tenant.` : '.'}
                </Typography>
              </Box>
            )}
          </List>
          {rows.length > limit && (
            <Box sx={{ p: 1.5, textAlign: 'center' }}>
              <Button size="small" variant="outlined" onClick={() => setLimit((l) => l + PAGE_SIZE)}>
                Show {Math.min(PAGE_SIZE, rows.length - limit).toLocaleString()} more
                &nbsp;({(rows.length - limit).toLocaleString()} remaining)
              </Button>
              <Typography variant="caption" sx={{ display: 'block', color: GREY, mt: 0.5 }}>
                Export to CSV for all {rows.length.toLocaleString()}.
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>

      {/* ---- More filters: the long tail, every field on the type ---------- */}
      <Dialog open={moreOpen} onClose={() => setMoreOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ color: HEAD, fontWeight: 700 }}>More filters</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ color: GREY, mb: 2 }}>
            All {advFields.length} fields on {scopeTypes.map((t) => typeMeta(t).label).join(', ')} —
            same operators and the same matching rules as Saved Views, so a filter behaves
            identically in both places. These AND with the facets on the left.
          </Typography>
          {!filters.length && (
            <Typography variant="body2" sx={{ color: GREY, mb: 2 }}>
              No extra filters yet.
            </Typography>
          )}
          {filters.map((f, i) => {
            const opts = optionsFor(f.field);
            const needsValue = VALUELESS.indexOf(f.op) === -1;
            // A filter can outlive a type change; keep its field selectable.
            const choices = advFieldMap[f.field]
              ? advFields
              : advFields.concat([{ key: f.field, label: f.field, section: 'other type' }]);
            return (
              // eslint-disable-next-line react/no-array-index-key
              <Stack key={i} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <TextField
                  select size="small" label="Field" value={f.field || ''} sx={{ flex: 1.4, minWidth: 170, width: '100%' }}
                  onChange={(e) => setFilter(i, { field: e.target.value, value: '' })}
                >
                  {choices.map((c) => (
                    <MenuItem key={c.key} value={c.key}>
                      {c.label}
                      {c.section ? <span style={{ color: '#9AA6B2', fontSize: 11, marginLeft: 6 }}>{c.section}</span> : null}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select size="small" label="Operator" value={f.op} sx={{ width: { xs: '100%', sm: 150 } }}
                  onChange={(e) => setFilter(i, { op: e.target.value })}
                >
                  {OPERATORS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </TextField>
                {needsValue && (opts.length ? (
                  <TextField
                    select size="small" label="Value" value={f.value || ''} sx={{ flex: 1, minWidth: 150, width: '100%' }}
                    onChange={(e) => setFilter(i, { value: e.target.value })}
                  >
                    <MenuItem value="">— any —</MenuItem>
                    {opts.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                  </TextField>
                ) : (
                  <TextField
                    size="small" label="Value" value={f.value || ''} sx={{ flex: 1, minWidth: 150, width: '100%' }}
                    onChange={(e) => setFilter(i, { value: e.target.value })}
                  />
                ))}
                {!needsValue && <Box sx={{ flex: 1 }} />}
                <Tooltip title="Remove filter">
                  <IconButton size="small" onClick={() => removeFilter(i)}><DeleteIcon sx={{ fontSize: 18 }} /></IconButton>
                </Tooltip>
              </Stack>
            );
          })}
          <Button size="small" startIcon={<AddIcon />} onClick={addFilter}>Add filter</Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFilters([])} disabled={!filters.length}>Clear</Button>
          <Button variant="contained" sx={{ bgcolor: BLUE, '&:hover': { bgcolor: NAVY } }} onClick={() => setMoreOpen(false)}>
            Done — {rows.length.toLocaleString()} match
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import is a WRITE. Opened from the master workspace this used to
          redirect the entire upload into the 'nghs' client — a shortcut from
          when nghs was the only tenant with data. An import that silently lands
          in the wrong client's estate is not recoverable by the person who did
          it, so the dialog only opens where we may legitimately author. */}
      <ImportDialog
        open={importOpen && tenantId !== 'optimum'}
        onClose={() => setImportOpen(false)}
        tenantId={tenantId}
        onImported={reload}
      />
    </Box>
  );
}
